/* =========================================================
   Inicio de sesión: una sola puerta para los dos modos.

   En modo demo se compara contra las identidades de prueba, como
   siempre. En modo API se llama al servidor, y si autentica se
   hidrata el alcance del usuario antes de dejarlo entrar — así la
   primera pantalla ya tiene datos y no parpadea.

   Las pantallas no saben en qué modo están: reciben el mismo
   `DemoUser` en los dos casos.
   ========================================================= */
import type { DemoUser, Role } from '@/types';
import { DEMO_USERS, IS_API, apiSource } from '@/data';
import { refreshSession, signIn, signOut } from './api';

export type SignInOutcome =
  /** `mustChangePassword` llega de una cuenta recién dada de alta: entró
   *  con la contraseña temporal del CSV y hay que pedirle una propia
   *  antes de dejarla usar la aplicación. */
  | { kind: 'ok'; user: DemoUser; mustChangePassword?: boolean }
  | { kind: 'totp' }
  | { kind: 'error'; message: string };

/** El rol lo decide el servidor; aquí solo se reconoce.
 *
 *  SITE_ADMIN se traducía a SITE_MGR, y SITE_MGR es de solo lectura: la
 *  cuenta perdía en la pantalla el permiso de editar su site que el
 *  servidor sí le concede, y salía nombrada como «Site Manager».
 *
 *  Lo que no se reconoce cae en SITE_MGR, el más restringido: equivocarse
 *  hacia el lado que no deja escribir se nota y se corrige; hacia el otro,
 *  no se nota. */
const asRole = (raw: string): Role =>
  raw === 'ETC_ADMIN' || raw === 'PARTNER_MGR' || raw === 'SITE_ADMIN' ? raw : 'SITE_MGR';

export async function attemptSignIn(
  username: string,
  password: string,
  totp?: string,
): Promise<SignInOutcome> {
  if (!IS_API) {
    const user = DEMO_USERS.find((x) => x.username === username && x.password === password);
    return user ? { kind: 'ok', user } : { kind: 'error', message: 'credentials' };
  }

  const res = await signIn(username, password, totp);
  if (res.kind === 'totp') return { kind: 'totp' };
  if (res.kind === 'error') return { kind: 'error', message: res.message };

  const ds = apiSource();
  if (ds) {
    try {
      await ds.hydrate();
    } catch {
      return { kind: 'error', message: 'No se pudieron cargar los datos de tu alcance' };
    }
  }

  const u = res.user;
  return {
    kind: 'ok',
    user: {
      username: u.Email,
      password: '',
      role: asRole(u.role),
      name: `${u.FirstName} ${u.LastName}`.trim(),
      orgId: u.orgIds[0] ?? null,
      orgIds: u.orgIds,
      siteId: u.siteIds[0],
      puedeEditarOrg: u.puedeEditarOrg,
    },
    mustChangePassword: Boolean(u.mustChangePassword),
  };
}

export async function endSession() {
  if (IS_API) await signOut();
}

/** Recupera una sesión existente tras recargar la página.
 *
 *  El access token vive solo en memoria, así que una recarga lo pierde:
 *  se pide uno nuevo con la cookie de refresco y se vuelve a hidratar.
 *  Devuelve false si la sesión ya no vale, y entonces toca volver al
 *  acceso — el usuario guardado en el navegador no es una sesión.
 */
export async function restoreSession(): Promise<DemoUser | null | 'sin-cambios'> {
  if (!IS_API) return 'sin-cambios';
  const ds = apiSource();
  if (!ds) return 'sin-cambios';
  if (ds.hydrated) return 'sin-cambios';

  const u = await refreshSession();
  if (!u) return null;
  try {
    await ds.hydrate();
  } catch {
    return null;
  }
  // El rol y el alcance se toman de lo que acaba de responder el servidor,
  // no de lo guardado en el navegador: esa copia la puede editar su dueño,
  // y con ella se abrirían pantallas de administración ajenas.
  return {
    username: u.Email,
    password: '',
    role: asRole(u.role),
    name: `${u.FirstName} ${u.LastName}`.trim(),
    orgId: u.orgIds[0] ?? null,
    orgIds: u.orgIds,
    siteId: u.siteIds[0],
    puedeEditarOrg: u.puedeEditarOrg,
  };
}
